/**
 * Main Application Entry Point for TitCock.tv
 *
 * This module:
 * - Initializes all DOM element references
 * - Sets up all event listeners
 * - Coordinates between modules
 * - Handles application startup
 */

import { $, $$, debounce } from "./utils.js";
import {
  elements,
  CONFIG,
  playerState,
  savedState,
  viewsState,
  feedState,
  getCards,
  getVideo,
  getCurrentCard,
  isGateVisible,
} from "./state.js";
import {
  loadState,
  isCardLiked,
  saveLikedVideo,
  removeLikedVideo,
  isLiked,
} from "./storage.js";
import { initFeed, fetchFeed, fetchPost } from "./feed.js";
import {
  createCard,
  createVerticalVastAdCard,
  isAdCard,
  positionCards,
  activateCard,
  loadVideo,
  playVideo,
  toggleMute,
  activeVideoHasNoAudio,
  handlePointerDown,
  handlePointerMove,
  handlePointerUp,
  handleWheel,
  handleKeyDown,
  handleVisibilityChange,
  handleResize,
} from "./player.js";
import {
  renderGallery,
  openSavedViewer,
  closeSavedViewer,
  handleSavedPointerDown,
  handleSavedPointerMove,
  handleSavedPointerUp,
  handleSavedWheel,
  handleSavedKeyDown,
  handleSavedResize,
  scrollSaved,
} from "./gallery.js";
import {
  notify,
  bounce,
  switchToView,
  updateUIVisibility,
  updateLikeIcon,
  updateLikeCount,
  showLikeBurst,
  updateHeader,
  initUI,
  toggleFullscreen,
  handleGlobalSwipeDown,
  handleGlobalSwipeMove,
  handleGlobalSwipeUp,
  isSwipeActive,
  cancelGlobalSwipe,
  showConfirmModal,
} from "./ui.js";

// ==========================================================================
// DOM Element Initialization
// ==========================================================================

/**
 * Initialize all DOM element references
 */
const initElements = () => {
  elements.gate = $("gate");
  elements.scroller = $("scroller");
  elements.content = $("content");
  elements.headerTitle = $("headerTitle");
  elements.headerAuthor = $("headerAuthor");
  elements.categoryFilter = $("categoryFilter");
  elements.headerCategory = $("headerCategory");
  elements.categoryMenu = $("categoryMenu");
  elements.header = $$("header");
  elements.globalControls = $$(".global-controls");
  elements.muteBtn = $("muteBtn");
  elements.muteIcon = $("muteIcon");
  elements.shareBtn = $("shareBtn");
  elements.shareIcon = $("shareIcon");
  elements.likeBtn = $("likeBtn");
  elements.likeIcon = $("likeIcon");
  elements.likeCount = $("likeCount");
  elements.notifier = $("notifier");
  elements.fsBtn = $("fsBtn");
  elements.fsIcon = $("fsIcon");
  elements.savedIcon = $("savedIcon");
  elements.settingsIcon = $("settingsIcon");
  elements.homeBtn = $("homeBtn");
  elements.homeIcon = $("homeIcon");
  elements.savedBtn = $("savedBtn");
  elements.settingsBtn = $("settingsBtn");
  elements.viewsSlider = $("viewsSlider");
  elements.galleryGrid = $("galleryGrid");
  elements.galleryContainer = $("galleryContainer");
  elements.savedEmpty = $("savedEmpty");
  elements.savedViewer = $("savedViewer");
  elements.savedViewerBack = $("savedViewerBack");
  elements.savedViewerContent = $("savedViewerContent");
  elements.footer = $$("footer");
  elements.settingsHome = $("settingsHome");
  elements.legalBtn = $("legalBtn");
  elements.legalView = $("legalView");
  elements.legalBackBtn = $("legalBackBtn");
};

// ==========================================================================
// Gate Handling
// ==========================================================================

const setupGateHandlers = () => {
  const gate = elements.gate;
  if (!gate) return;

  const main = $("gateMain");
  const legal = $("gateLegal");

  gate.querySelector(".legal-link")?.addEventListener("click", () => {
    main?.classList.add("hidden");
    legal?.classList.remove("hidden");
  });

  gate.querySelector(".gate-back")?.addEventListener("click", () => {
    legal?.classList.add("hidden");
    main?.classList.remove("hidden");
  });

  gate.querySelector(".leave")?.addEventListener("click", () => {
    gate.style.opacity = "0";
    setTimeout(() => gate.remove(), 220);
  });
};

// ==========================================================================
// Button Action Queue
// ==========================================================================

const buttonActionQueue = [];
let isProcessingQueue = false;

const queueButtonAction = (action) => {
  if (!playerState.isAnimating && !playerState.gesture.active) {
    // Not animating, execute immediately
    action();
  } else {
    // Queue for later
    buttonActionQueue.push(action);
  }
};

const processButtonQueue = () => {
  if (isProcessingQueue || buttonActionQueue.length === 0) return;
  isProcessingQueue = true;

  while (buttonActionQueue.length > 0) {
    const action = buttonActionQueue.shift();
    try {
      action();
    } catch (err) {
      console.error("Error executing queued button action:", err);
    }
  }

  isProcessingQueue = false;
};

// Watch for animation completion to process queue
const watchAnimationComplete = () => {
  const checkAndProcess = () => {
    if (!playerState.isAnimating && !playerState.gesture.active) {
      processButtonQueue();
    }
  };

  setInterval(checkAndProcess, 100);
};

// ==========================================================================
// Share Functionality
// ==========================================================================

const getShareUrl = () => {
  const card = getCurrentCard();
  if (!card) return "";
  if (isAdCard(card)) return "";

  const slug = card.dataset.slug;
  return slug
    ? `${location.origin}${location.pathname}#/video/${encodeURIComponent(slug)}`
    : "";
};

const handleShare = async () => {
  const link = getShareUrl();
  if (!link) return;

  try {
    await navigator.clipboard.writeText(link);
  } catch {
    // Fallback for older browsers
    const ta = document.createElement("textarea");
    ta.value = link;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }

  bounce(elements.shareIcon);
  notify("Video link copied!", 2200);
};

// ==========================================================================
// Like Functionality
// ==========================================================================

const handleLike = async (ev, { allowUnlike = false, showBurst = true } = {}) => {
  const card = getCurrentCard();
  if (!card) return;
  if (isAdCard(card)) return;

  const slug = card.dataset.slug;
  if (!slug) return;

  const wasLiked = isLiked(slug);

  if (wasLiked) {
    if (!allowUnlike) {
      if (showBurst) showLikeBurst(ev?.clientX, ev?.clientY);
      bounce(elements.likeIcon);
      return;
    }

    const removeLike = () => {
      removeLikedVideo(slug);
      updateLikeIcon(false);
      updateLikeCount(card.dataset.likes, false);
      bounce(elements.likeIcon);
      if (savedState.isViewerActive) {
        closeSavedViewer();
      }
      renderGallery();
    };

    if (savedState.isViewerActive) {
      showConfirmModal({
        title: "Remove from Liked?",
        message: "This will delete this video from your saved collection.",
        confirmText: "Remove",
        onConfirm: removeLike,
      });
    } else {
      removeLike();
    }
    return;
  }

  saveLikedVideo(slug, {
    name: card.dataset.name || "",
    category: card.dataset.category || "",
    categoryLabel: card.dataset.categoryLabel || "",
    title: card.dataset.title || "",
    author: card.dataset.author || "",
    likes: Number(card.dataset.likes) || 0,
    redgifsUrl: card.dataset.redgifsUrl || "",
    videoSrc: card.querySelector("video")?.dataset?.src || "",
    thumbnail: card.dataset.thumbnail || "",
  });

  updateLikeIcon(true);
  updateLikeCount(card.dataset.likes, true);
  if (showBurst) showLikeBurst(ev?.clientX, ev?.clientY);

  bounce(elements.likeIcon);
  renderGallery();
};

// ==========================================================================
// Feed Loading
// ==========================================================================

let isLoadingMoreContent = false;
let videosSinceLastAd = 0;
let feedRevision = 0;
let pendingFeedRefresh = false;

const createFeedCards = (videos) => {
  const cards = [];

  for (const videoData of videos) {
    cards.push(createCard(videoData));
    videosSinceLastAd++;

    if (videosSinceLastAd >= CONFIG.VAST_AD_INTERVAL) {
      cards.push(createVerticalVastAdCard());
      videosSinceLastAd = 0;
    }
  }

  return cards;
};

const loadMoreContent = async () => {
  // Prevent redundant calls while already loading
  if (isLoadingMoreContent) {
    console.log("[Feed] Already loading, skipping redundant call");
    return;
  }

  isLoadingMoreContent = true;
  const revision = feedRevision;
  console.log("[Feed] Loading more content...");

  try {
    const videos = await fetchFeed();
    if (revision !== feedRevision) {
      videos.forEach((video) => feedState.seenSlugs.delete(video.slug));
      return;
    }
    if (!videos.length) return;

    const cards = getCards();
    const syncCards = [];
    const asyncCards = [];
    let builtSync = 0;

    for (const card of createFeedCards(videos)) {
      if (!playerState.firstCardReady && cards.length === 0) {
        elements.content?.appendChild(card);
        loadVideo(getVideo(card), true);
        playerState.firstCardReady = true;
      } else if (builtSync < CONFIG.MAX_SYNC_CARDS) {
        syncCards.push(card);
        builtSync++;
      } else {
        asyncCards.push(card);
      }
    }

    if (syncCards.length) {
      const frag = document.createDocumentFragment();
      syncCards.forEach((c) => frag.appendChild(c));
      elements.content?.appendChild(frag);
    }

    if (asyncCards.length) {
      const processAsync = () => {
        if (revision !== feedRevision) return;
        const frag = document.createDocumentFragment();
        asyncCards.forEach((c) => frag.appendChild(c));
        elements.content?.appendChild(frag);
      };

      if ("requestIdleCallback" in window) {
        requestIdleCallback(processAsync, { timeout: 500 });
      } else {
        setTimeout(processAsync, 0);
      }
    }

    const finalCards = getCards();
    console.log(`[Feed] Final cards count after adding: ${finalCards.length}`);

    if (finalCards.length) {
      positionCards(playerState.currentIndex, true);

      // Immediately load videos around current position to prevent spinner stuck
      const currentIdx = playerState.currentIndex;
      const neighbors = [];
      // Preload larger range (-2 to +8) to handle rapid scrolling
      for (let i = -2; i <= 8; i++) {
        if (finalCards[currentIdx + i]) {
          neighbors.push(finalCards[currentIdx + i]);
        }
      }

      // Cleanup distant videos to free up network resources
      finalCards.forEach((card, index) => {
        if (Math.abs(index - currentIdx) > 12) {
          const vid = getVideo(card);
          if (vid && vid.hasAttribute("src")) {
            vid.removeAttribute("src");
            vid.load();
          }
        }
      });

      neighbors.forEach((card) => {
        const vid = getVideo(card);
        if (vid && !vid.src) {
          console.log(`[Feed] Preloading video for neighbor card`);
          loadVideo(vid, true);
        }
      });
    }
  } finally {
    isLoadingMoreContent = false;
    if (pendingFeedRefresh) {
      pendingFeedRefresh = false;
      loadMoreContent();
    }
  }
};

const refillFutureFeed = () => {
  feedRevision++;

  const cards = getCards();
  const futureCards = cards.slice(playerState.currentIndex + 1);
  futureCards.forEach((card) => {
    if (!isAdCard(card) && card.dataset.slug) {
      feedState.seenSlugs.delete(card.dataset.slug);
    }
    card.remove();
  });

  videosSinceLastAd = 0;
  const retainedCards = getCards();
  for (let index = retainedCards.length - 1; index >= 0; index--) {
    if (isAdCard(retainedCards[index])) break;
    videosSinceLastAd++;
  }
  videosSinceLastAd %= CONFIG.VAST_AD_INTERVAL;

  positionCards(playerState.currentIndex, true);
  pendingFeedRefresh = feedState.selectedCategories.size > 0;
  if (!isLoadingMoreContent && pendingFeedRefresh) {
    pendingFeedRefresh = false;
    loadMoreContent();
  }
};

const setCategoryMenuOpen = (open) => {
  elements.categoryFilter?.classList.toggle("open", open);
  elements.headerCategory?.setAttribute("aria-expanded", String(open));
  elements.categoryMenu?.setAttribute("aria-hidden", String(!open));
};

const setupCategoryFilter = () => {
  const inputs = [...(elements.categoryMenu?.querySelectorAll("input") || [])];
  inputs.forEach((input) => {
    input.checked = feedState.selectedCategories.has(input.value);
  });

  elements.headerCategory?.addEventListener("click", (event) => {
    event.stopPropagation();
    const isOpen = elements.categoryFilter?.classList.contains("open") || false;
    setCategoryMenuOpen(!isOpen);
  });

  elements.categoryMenu?.addEventListener("change", (event) => {
    const input = event.target.closest('input[type="checkbox"]');
    if (!input) return;

    if (input.checked) {
      feedState.selectedCategories.add(input.value);
    } else {
      feedState.selectedCategories.delete(input.value);
    }
    refillFutureFeed();
  });

  document.addEventListener("click", (event) => {
    if (!event.target.closest("#categoryFilter")) setCategoryMenuOpen(false);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") setCategoryMenuOpen(false);
  });
};

// ========================================================================== 
// Deep Link Handling
// ==========================================================================

const getDeepLinkUrl = () => {
  const match = /^#\/video\/([^/?#]+)$/i.exec(location.hash);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

const navigateToDeepLink = (targetSlug) => {
  const normalizedTarget = String(targetSlug).toLowerCase();
  const cards = getCards();

  const index = cards.findIndex(
    (card) => card.dataset.slug?.toLowerCase() === normalizedTarget,
  );

  if (index !== -1) {
    playerState.currentIndex = index;
    activateCard(index, loadMoreContent);
    positionCards(index, true);
    return true;
  }

  return false;
};

// ==========================================================================
// View Switching Callbacks
// ==========================================================================

const handleBeforeViewSwitch = async (fromView, toView) => {
  if (savedState.isViewerActive) {
    closeSavedViewer();
  }

  // Reset animation state when leaving home to prevent scroll lock
  if (fromView === "home") {
    const activeCard = getCards()[playerState.activeIndex];
    const vid = getVideo(activeCard);
    if (vid) vid.pause();

    playerState.isAnimating = false;
    playerState.animationId = (playerState.animationId || 0) + 1;
    playerState.gesture.active = false;
  }

  // Clear saved viewer cards (they are independent copies, not from home feed)
  if (fromView === "saved") {
    savedState.cards = [];
    savedState.currentIndex = 0;
    savedState.activeIndex = -1;
    savedState.isAnimating = false;
    savedState.animationId = (savedState.animationId || 0) + 1;
    savedState.gesture.active = false;
  }
};

const handleAfterViewSwitch = async (targetView) => {
  if (targetView === "saved") {
    // renderGallery();
  } else if (targetView === "home") {
    playerState.isAnimating = false;
    playerState.gesture.active = false;
    positionCards(playerState.currentIndex, true);

    const card = getCards()[playerState.currentIndex];
    const vid = getVideo(card);
    if (vid) {
      // Resume playback when returning to home
      if (playerState.activeIndex === playerState.currentIndex) {
        playVideo(vid);
      }
    }
    if (card) {
      updateHeader({
        slug: isAdCard(card) ? "" : card.dataset.slug || "",
        categoryLabel: card.dataset.categoryLabel || "",
        author: card.dataset.author || "",
        title: card.dataset.title || "",
        likes: card.dataset.likes,
        isLiked: isCardLiked(card),
      });
    }
  }
};

const showSettingsHome = () => {
  elements.settingsHome?.classList.remove("hidden");
  elements.legalView?.classList.add("hidden");
};

const showLegalView = () => {
  elements.settingsHome?.classList.add("hidden");
  elements.legalView?.classList.remove("hidden");
};

// ==========================================================================
// Event Listeners Setup
// ==========================================================================

const setupEventListeners = () => {
  setupCategoryFilter();

  // Mute button
  elements.muteBtn?.addEventListener("click", () => {
    queueButtonAction(() => {
      if (activeVideoHasNoAudio()) {
        notify("Video has no audio source", 2200);
        return;
      }
      toggleMute();
      bounce(elements.muteIcon);
    });
  });

  // Share button
  elements.shareBtn?.addEventListener("click", () => {
    queueButtonAction(handleShare);
  });

  // Like button
  elements.likeBtn?.addEventListener("click", async (e) => {
    queueButtonAction(async () => {
      await handleLike(e, { allowUnlike: true, showBurst: false });
      renderGallery();
    });
  });

  // Fullscreen button
  elements.fsBtn?.addEventListener("click", () => {
    queueButtonAction(() => {
      bounce(elements.fsIcon);
      toggleFullscreen();
    });
  });

  // Footer navigation
  elements.homeBtn?.addEventListener("click", () => {
    bounce(elements.homeIcon);
    if (viewsState.currentView !== "home") {
      switchToView("home", {
        onBeforeSwitch: handleBeforeViewSwitch,
        onAfterSwitch: handleAfterViewSwitch,
      });
    }
  });

  elements.savedBtn?.addEventListener("click", () => {
    bounce(elements.savedIcon);
    if (viewsState.currentView !== "saved") {
      switchToView("saved", {
        onBeforeSwitch: handleBeforeViewSwitch,
        onAfterSwitch: handleAfterViewSwitch,
      });
    }
  });

  elements.settingsBtn?.addEventListener("click", () => {
    bounce(elements.settingsIcon);
    showSettingsHome();
    if (viewsState.currentView !== "settings") {
      switchToView("settings", {
        onBeforeSwitch: handleBeforeViewSwitch,
        onAfterSwitch: handleAfterViewSwitch,
      });
    }
  });

  // Saved viewer back button
  elements.savedViewerBack?.addEventListener("click", (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    closeSavedViewer();
  });

  // Global horizontal swipe gestures for view switching
  document.addEventListener(
    "pointerdown",
    (ev) => {
      // Don't start swipe if clicking on interactive elements
      if (ev.target.closest("button, a, input, .gate, .category-menu")) return;
      handleGlobalSwipeDown(ev);
    },
    { passive: true },
  );

  document.addEventListener(
    "pointermove",
    (ev) => {
      // Always call move handler if swipe started - it determines direction
      if (!viewsState.swipe.active) return;
      handleGlobalSwipeMove(ev);
    },
    { passive: false },
  );

  document.addEventListener(
    "pointerup",
    (ev) => {
      // Handle swipe end if swipe was started
      if (viewsState.swipe.active) {
        handleGlobalSwipeUp(ev, {
          onBeforeSwitch: handleBeforeViewSwitch,
          onAfterSwitch: handleAfterViewSwitch,
        });
      }
    },
    { passive: true },
  );

  document.addEventListener(
    "pointercancel",
    () => {
      if (viewsState.swipe.active) {
        cancelGlobalSwipe();
      }
    },
    { passive: true },
  );

  // Scroller gestures (home view) - vertical scrolling only
  elements.scroller?.addEventListener(
    "pointerdown",
    (ev) => {
      if (viewsState.currentView !== "home" || viewsState.isSwitching) return;
      handlePointerDown(ev);
    },
    { passive: false },
  );

  elements.scroller?.addEventListener(
    "pointermove",
    (ev) => {
      if (viewsState.currentView !== "home") return;
      // Only handle vertical movement if not in a horizontal swipe
      if (isSwipeActive()) return;
      handlePointerMove(ev, loadMoreContent);
    },
    { passive: false },
  );

  elements.scroller?.addEventListener(
    "pointerup",
    (ev) => {
      if (viewsState.currentView !== "home") return;
      if (isSwipeActive()) return;
      handlePointerUp(ev, loadMoreContent, handleLike);
    },
    { passive: false },
  );

  elements.scroller?.addEventListener(
    "pointercancel",
    (ev) => {
      if (viewsState.currentView !== "home") return;
      if (isSwipeActive()) return;
      handlePointerUp(ev, loadMoreContent, handleLike);
    },
    { passive: false },
  );

  elements.scroller?.addEventListener(
    "wheel",
    (ev) => {
      if (viewsState.currentView !== "home") return;
      handleWheel(ev, loadMoreContent);
    },
    { passive: false },
  );

  // Saved viewer gestures - vertical scrolling only
  elements.savedViewer?.addEventListener("pointerdown", handleSavedPointerDown);
  elements.savedViewer?.addEventListener("pointermove", (ev) => {
    if (isSwipeActive()) return;
    handleSavedPointerMove(ev);
  });
  elements.savedViewer?.addEventListener("pointerup", (ev) => {
    if (isSwipeActive()) return;
    handleSavedPointerUp(ev, handleLike);
  });
  elements.savedViewer?.addEventListener("pointercancel", (ev) => {
    if (isSwipeActive()) return;
    handleSavedPointerUp(ev, handleLike);
  });
  elements.savedViewer?.addEventListener("wheel", handleSavedWheel, {
    passive: false,
  });

  // Global drag prevention
  document.addEventListener("dragstart", (ev) => ev.preventDefault());

  // Keyboard navigation
  document.addEventListener("keydown", (ev) => {
    if (isGateVisible()) return;

    // Try saved viewer first
    if (handleSavedKeyDown(ev)) return;

    // View switching with arrow keys
    if (!savedState.isViewerActive) {
      if (ev.key === "ArrowLeft") {
        ev.preventDefault();
        if (viewsState.currentView === "saved") {
          switchToView("home", {
            onBeforeSwitch: handleBeforeViewSwitch,
            onAfterSwitch: handleAfterViewSwitch,
          });
        } else if (viewsState.currentView === "settings") {
          switchToView("saved", {
            onBeforeSwitch: handleBeforeViewSwitch,
            onAfterSwitch: handleAfterViewSwitch,
          });
        }
        return;
      }

      if (ev.key === "ArrowRight") {
        ev.preventDefault();
        if (viewsState.currentView === "home") {
          switchToView("saved", {
            onBeforeSwitch: handleBeforeViewSwitch,
            onAfterSwitch: handleAfterViewSwitch,
          });
        } else if (viewsState.currentView === "saved") {
          switchToView("settings", {
            onBeforeSwitch: handleBeforeViewSwitch,
            onAfterSwitch: handleAfterViewSwitch,
          });
        }
        return;
      }
    }

    // Player keyboard handling
    if (viewsState.currentView === "home") {
      handleKeyDown(ev, {
        onNeedMore: loadMoreContent,
      });
    }
  });

  // Visibility change (pause when tab hidden)
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Window resize
  window.addEventListener(
    "resize",
    debounce(() => {
      handleResize();
      handleSavedResize();
    }, 100),
    { passive: true },
  );

  elements.legalBtn?.addEventListener("click", showLegalView);
  elements.legalBackBtn?.addEventListener("click", showSettingsHome);
};

// ==========================================================================
// Application Initialization
// ==========================================================================

const init = async () => {
  // Initialize DOM references
  initElements();

  // Load persisted state
  loadState();

  // Setup gate handlers
  setupGateHandlers();

  // Initialize modules
  initUI();

  // Setup event listeners
  setupEventListeners();
  renderGallery();

  // Start button action queue watcher
  watchAnimationComplete();

  // Initialize feed
  await initFeed();

  // Load initial content
  await loadMoreContent();

  const cards = getCards();
  if (!cards.length) {
    if (elements.headerTitle) {
      elements.headerTitle.textContent = "Failed to load feed.";
    }
    notify("Could not load videos.", 5000);
    return;
  }

  // Pre-set opacity for the first video so it's ready when activated below
  const firstVid = getVideo(cards[0]);
  if (firstVid) {
    firstVid.style.opacity = "1";
  }

  // Handle deep link
  const deepLinkSlug = getDeepLinkUrl();
  let deepLinkHandled = false;

  if (deepLinkSlug) {
    // Try to find in loaded feed first
    if (navigateToDeepLink(deepLinkSlug)) {
      deepLinkHandled = true;
    } else {
      // Fetch the specific video if it is not in the loaded cards
      try {
        const videoData = await fetchPost(deepLinkSlug);

        if (videoData) {
          feedState.seenSlugs.add(videoData.slug);
          const card = createCard(videoData);
          const content = elements.content;
          if (content.firstChild) {
            content.insertBefore(card, content.firstChild);
          } else {
            content.appendChild(card);
          }

          playerState.currentIndex = 0;
          activateCard(0, loadMoreContent);
          positionCards(0, true);
          deepLinkHandled = true;
        }
      } catch (e) {
        console.error("Deep link fetch failed", e);
      }
    }
  }

  if (!deepLinkHandled) {
    playerState.currentIndex = 0;
    activateCard(0, loadMoreContent);
    positionCards(0, true);
  }

  // Start playing
  const currentVid = getVideo(cards[playerState.currentIndex]);
  if (currentVid) {
    playVideo(currentVid);
  }
};

// Start the application
init().catch(console.error);
